import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, Clock, ArrowRight, Sparkles, Ban } from 'lucide-react';

const EVENT_TYPES = [
  { id: 'all', name: 'Tous' },
  { id: 'menu_special', name: 'Menu spécial' },
  { id: 'soiree_thematique', name: 'Soirée thématique' },
  { id: 'privatisation', name: 'Privatisation' },
  { id: 'autre', name: 'Autre' }
];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const url = selectedType === 'all' 
          ? `${process.env.REACT_APP_BACKEND_URL}/api/events`
          : `${process.env.REACT_APP_BACKEND_URL}/api/events?event_type=${selectedType}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setEvents(data);
        }
      } catch (error) {
        console.error('Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [selectedType]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    return `${hours}h${minutes}`;
  };

  const upcomingEvents = events.filter(e => !e.is_past);
  const pastEvents = events.filter(e => e.is_past);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl tracking-[0.15em] uppercase text-gray-900 mb-6"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: '400' }}
          >
            Événements
          </h1>
          <p 
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: 'var(--font-body)', lineHeight: '1.8' }}
          >
            Découvrez nos soirées à thème, menus spéciaux et événements exclusifs
          </p>
        </div>
      </section>

      {/* Type Filter */}
      <section className="pb-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {EVENT_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                className={`px-5 py-2 rounded-full text-sm tracking-wide transition-all duration-300 ${
                  selectedType === type.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-6" />
              <h2 className="text-2xl text-gray-600 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Aucun événement à venir
              </h2>
              <p className="text-gray-500">
                Revenez bientôt pour découvrir nos prochains événements.
              </p>
            </div>
          ) : (
            <>
              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div className="space-y-8 mb-16">
                  {upcomingEvents.map((event) => (
                    <EventCard key={event.id} event={event} formatDate={formatDate} formatTime={formatTime} />
                  ))}
                </div>
              )}

              {/* Past Events */}
              {pastEvents.length > 0 && (
                <div className="mt-16 pt-16 border-t border-gray-200">
                  <h2 
                    className="text-2xl tracking-[0.1em] uppercase text-gray-500 mb-8 text-center"
                    style={{ fontFamily: 'Playfair Display, serif', fontWeight: '400' }}
                  >
                    Événements passés
                  </h2>
                  <div className="space-y-6 opacity-70">
                    {pastEvents.slice(0, 6).map((event) => (
                      <EventCard key={event.id} event={event} formatDate={formatDate} formatTime={formatTime} isPast />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const EventCard = ({ event, formatDate, formatTime, isPast = false }) => {
  return (
    <div 
      className={`bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-300 ${
        isPast ? '' : 'hover:shadow-md'
      }`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="w-full md:w-72 h-48 md:h-auto flex-shrink-0 relative">
          {event.cover_image_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${event.cover_image_url}`}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <Calendar className="w-12 h-12 text-gray-300" />
            </div>
          )}
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {event.show_nouveau_badge && !isPast && (
              <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
                <Sparkles size={12} /> Nouveau
              </span>
            )}
            {event.is_sold_out && !isPast && (
              <span className="flex items-center gap-1 bg-red-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
                <Ban size={12} /> Complet
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <div>
              <h3 
                className="text-2xl md:text-3xl text-gray-900 mb-3"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: '500' }}
              >
                {event.title}
              </h3>
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <span className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span style={{ fontFamily: 'var(--font-body)' }}>{formatDate(event.event_date)}</span>
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={16} className="text-gray-400" />
                  <span style={{ fontFamily: 'var(--font-body)' }}>{formatTime(event.event_time)}</span>
                </span>
              </div>
            </div>
            {event.price_per_person && (
              <div 
                className="text-2xl md:text-3xl text-gray-900 font-semibold whitespace-nowrap"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {event.price_per_person}
                <span className="text-sm text-gray-500 font-normal ml-1">/ pers.</span>
              </div>
            )}
          </div>

          {event.description && (
            <p 
              className="text-gray-600 mb-6 line-clamp-3"
              style={{ fontFamily: 'var(--font-body)', lineHeight: '1.7' }}
            >
              {event.description}
            </p>
          )}

          {/* Actions */}
          {!isPast && (
            <div className="flex flex-wrap items-center gap-4">
              {event.pdf_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${event.pdf_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 text-sm tracking-wide hover:bg-gray-50 transition-colors"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  Voir le menu
                </a>
              )}
              {!event.is_sold_out && (
                <a
                  href="/#reservation"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm tracking-wide hover:bg-gray-800 transition-colors"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  Réserver
                  <ArrowRight size={16} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
